import React, { useCallback, useRef, useState } from "react";
import { Plus, X, User } from "lucide-react";

// value: { eixo_x_label, eixo_y_label, points: [{ id, name, x, y, isSelf }] }
// x,y ∈ [0,100]

function genId() {
  return "p_" + Math.random().toString(36).slice(2, 9);
}

export default function PerceptualMap({ value = {}, onChange }) {
  const svgRef = useRef(null);
  const dragId = useRef(null);
  const [newName, setNewName] = useState("");

  const points = Array.isArray(value.points) ? value.points : [];
  const xLabel = value.eixo_x_label || "Eixo X (esquerda ↔ direita)";
  const yLabel = value.eixo_y_label || "Eixo Y (baixo ↔ topo)";

  const ensureSelf = () => {
    if (points.some((p) => p.isSelf)) return points;
    return [{ id: "self", name: "Sua marca", x: 50, y: 50, isSelf: true }, ...points];
  };

  const emit = (nextPoints, patch = {}) => {
    onChange({ ...value, points: nextPoints, ...patch });
  };

  const addPoint = () => {
    if (!newName.trim()) return;
    const all = ensureSelf();
    emit([...all, { id: genId(), name: newName.trim(), x: 30 + Math.random() * 40, y: 30 + Math.random() * 40, isSelf: false }]);
    setNewName("");
  };

  const removePoint = (id) => {
    emit(points.filter((p) => p.id !== id));
  };

  const startDrag = (id) => (e) => {
    e.preventDefault();
    dragId.current = id;
  };

  const onMove = useCallback((e) => {
    if (!dragId.current || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    let x = ((clientX - rect.left) / rect.width) * 100;
    let y = 100 - ((clientY - rect.top) / rect.height) * 100;
    x = Math.max(2, Math.min(98, x));
    y = Math.max(2, Math.min(98, y));
    const id = dragId.current;
    const all = ensureSelf();
    emit(all.map((p) => p.id === id ? { ...p, x, y } : p));
  }, [points]); // eslint-disable-line

  const endDrag = () => { dragId.current = null; };

  React.useEffect(() => {
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", endDrag);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", endDrag);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", endDrag);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", endDrag);
    };
  }, [onMove]);

  const all = ensureSelf();
  // If we added self via ensureSelf and it wasn't in the original, propagate it
  React.useEffect(() => {
    if (!points.some((p) => p.isSelf) && points.length > 0) {
      emit([{ id: "self", name: "Sua marca", x: 50, y: 50, isSelf: true }, ...points]);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div className="space-y-3" data-testid="perceptual-map">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted font-mono">Eixo horizontal (— ↔ +)</label>
          <input className="input mt-1 text-sm" placeholder="Ex: Acessível ↔ Premium"
            value={xLabel === "Eixo X (esquerda ↔ direita)" ? "" : xLabel}
            onChange={(e) => emit(all, { eixo_x_label: e.target.value })}
            data-testid="axis-x-label" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-muted font-mono">Eixo vertical (— ↔ +)</label>
          <input className="input mt-1 text-sm" placeholder="Ex: Genérico ↔ Especialista"
            value={yLabel === "Eixo Y (baixo ↔ topo)" ? "" : yLabel}
            onChange={(e) => emit(all, { eixo_y_label: e.target.value })}
            data-testid="axis-y-label" />
        </div>
      </div>

      <div className="flex gap-2">
        <input className="input text-sm" placeholder="Nome do concorrente" value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPoint())}
          data-testid="perceptual-new-name" />
        <button type="button" onClick={addPoint} className="btn-grad text-xs flex items-center gap-1" data-testid="perceptual-add"><Plus size={12} /> Adicionar</button>
      </div>

      {/* SVG canvas */}
      <div className="card-2 p-4 pb-10">
        <div className="relative" style={{ aspectRatio: "1", maxWidth: 520, margin: "0 auto" }}>
          <svg ref={svgRef} viewBox="0 0 100 100" className="w-full h-full block" preserveAspectRatio="none" style={{ background: "var(--panel)", borderRadius: 8, cursor: dragId.current ? "grabbing" : "default" }}>
            {/* Grid */}
            {[25, 50, 75].map((v) => (
              <React.Fragment key={v}>
                <line x1={v} y1={0} x2={v} y2={100} stroke="var(--border)" strokeWidth="0.15" />
                <line x1={0} y1={v} x2={100} y2={v} stroke="var(--border)" strokeWidth="0.15" />
              </React.Fragment>
            ))}
            {/* Axes */}
            <line x1={50} y1={0} x2={50} y2={100} stroke="var(--muted)" strokeWidth="0.3" strokeDasharray="1 1" />
            <line x1={0} y1={50} x2={100} y2={50} stroke="var(--muted)" strokeWidth="0.3" strokeDasharray="1 1" />

            {/* Points */}
            {all.map((p) => (
              <g key={p.id} transform={`translate(${p.x}, ${100 - p.y})`}
                 style={{ cursor: "grab" }}
                 onMouseDown={startDrag(p.id)} onTouchStart={startDrag(p.id)}
                 data-testid={`point-${p.id}`}>
                <circle r={p.isSelf ? 3.5 : 2.8}
                  fill={p.isSelf ? "url(#selfGrad)" : "var(--panel)"}
                  stroke={p.isSelf ? "none" : "var(--muted)"}
                  strokeWidth="0.5" />
                {p.isSelf && (
                  <>
                    <defs>
                      <linearGradient id="selfGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0" stopColor="#FF7A5A" />
                        <stop offset="0.55" stopColor="#FF4E9E" />
                        <stop offset="1" stopColor="#C05BFF" />
                      </linearGradient>
                    </defs>
                  </>
                )}
                <text y={-5} textAnchor="middle" fontSize="2.6" fontWeight={p.isSelf ? 800 : 600}
                  fill={p.isSelf ? "#FF4E9E" : "currentColor"}>
                  {p.name}
                </text>
              </g>
            ))}
          </svg>

          {/* Axis labels */}
          <div className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-muted uppercase tracking-widest">—</div>
          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-muted uppercase tracking-widest">+</div>
          <div className="absolute left-1/2 -translate-x-1/2 bottom-1 text-[10px] font-mono text-muted uppercase tracking-widest">—</div>
          <div className="absolute left-1/2 -translate-x-1/2 top-1 text-[10px] font-mono text-muted uppercase tracking-widest">+</div>
          <div className="absolute -bottom-7 left-0 right-0 text-center text-xs text-muted">{xLabel}</div>
          <div className="absolute -left-4 top-0 bottom-0 flex items-center" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
            <span className="text-xs text-muted">{yLabel}</span>
          </div>
        </div>
      </div>

      {/* Points list */}
      {all.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {all.map((p) => (
            <span key={p.id} className={`chip flex items-center gap-1.5 ${p.isSelf ? "grad" : ""}`}>
              {p.isSelf && <User size={10} />}
              {p.name} <span className="text-[10px] opacity-70">({Math.round(p.x)}, {Math.round(p.y)})</span>
              {!p.isSelf && (
                <button onClick={() => removePoint(p.id)} className="opacity-70 hover:opacity-100"><X size={11} /></button>
              )}
            </span>
          ))}
        </div>
      )}
      <p className="text-[11px] text-muted">💡 Arraste os pontos pra reposicionar. A marca "Sua marca" (gradiente) é você — clique e arraste.</p>
    </div>
  );
}
